import React, { useState, useEffect, useCallback } from "react";
import { constants } from "../../utility/constants/constants";
import Container from "../components/Container";
import CompanyLogo from "../../Assets/icons/logo_large_black.svg";
import { Spinner } from "reactstrap";
import axios from "../../axios/axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { handleError } from "../../axios/handleError";

const FORGET_PASSWORD_COOLDOWN_KEY = "forgetPassword_cooldown_until";
const COOLDOWN_MS = 5 * 60 * 1000;

function getCooldownRemainingMs() {
  const raw = localStorage.getItem(FORGET_PASSWORD_COOLDOWN_KEY);
  if (!raw) return 0;
  const until = parseInt(raw, 10);
  if (!Number.isFinite(until)) return 0;
  return Math.max(0, until - Date.now());
}

function startCooldown() {
  localStorage.setItem(FORGET_PASSWORD_COOLDOWN_KEY, String(Date.now() + COOLDOWN_MS));
}

function formatCooldown(ms) {
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const ForgetPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);

  const syncCooldown = useCallback(() => {
    const remaining = getCooldownRemainingMs();
    setCooldownRemainingMs(remaining);
    if (remaining <= 0) {
      localStorage.removeItem(FORGET_PASSWORD_COOLDOWN_KEY);
    }
  }, []);

  useEffect(() => {
    syncCooldown();
    const id = setInterval(syncCooldown, 1000);
    return () => clearInterval(id);
  }, [syncCooldown]);

  const isCooldownActive = cooldownRemainingMs > 0;

  const handleOnSubmit = async (e) => {
    e.preventDefault();
    if (isCooldownActive) return;
    setLoading(true);
    try {
      const response = await axios.post(constants.FORGET_PASSWORD, { email: email.trim() }, { headers: { "Content-Type": "application/json" } });
      if (response.status === 200) {
        startCooldown();
        syncCooldown();
        setComplete(true);
      } else toast.error("Email Not Exist!");
    } catch (err) {
      handleError(err);
    }
    setLoading(false);
  };

  return (
    <Container>
      <div className="m-3 m-md-5 w-100">
        <img className="mb-4 cursor-pointer" onClick={() => window.open(constants.LANDING_PAGE_URL)} src={CompanyLogo} alt="logo" />
        <p className="h4 mb-4">Forgot Password</p>
        {complete ? (
          <>
            <p className="text-success fs-5">👍 An email containing a link to reset the password has been sent to you.</p>
            <p className="text-danger fs-6">NOTE: link will only be valid for one hour, after which you will need to request a new link to reset your password..</p>
            <Link className="fs-5" to="/login">
              Back to login
            </Link>
          </>
        ) : (
          <form className="d-flex flex-column justify-content-between" onSubmit={handleOnSubmit}>
            <div className="mb-4">
              <input
                autoFocus
                required
                type="email"
                pattern="^\S+@\S+\.\S+$"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Registered Email"
                className="form-control"
                disabled={isCooldownActive}
              />
            </div>
            {isCooldownActive && (
              <p className="text-muted small mb-3 mb-md-4">
                You can request another reset link after the cooldown. Time remaining: <strong>{formatCooldown(cooldownRemainingMs)}</strong>
              </p>
            )}
            <button disabled={loading || isCooldownActive} id="forgot_password_submit-btn" type="submit" className="btn btn-primary">
              {loading ? <Spinner size="sm" className="m-1" /> : "SUBMIT"}
            </button>
          </form>
        )}
      </div>
    </Container>
  );
};

export default ForgetPassword;
